import { useState } from "react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { EvidenceChip } from "@/components/Evidence";
import SponsorClimbDialog from "@/components/SponsorClimbDialog";
import { Film, Check, ArrowUpRight } from "lucide-react";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);

const THEMES = ["Adventure", "Discipline", "Outdoor Sports", "Sustainable Development", "Inspiration"];

const SUMMITS = [
  { peak: "Mount Everest", continent: "Asia", elevation: 8849, done: false },
  { peak: "Aconcagua", continent: "South America", elevation: 6961, done: false },
  { peak: "Denali", continent: "North America", elevation: 6190, done: false },
  { peak: "Kilimanjaro", continent: "Africa", elevation: 5895, done: true, note: "Summited 2023 · Uhuru Peak · flag documented" },
  { peak: "Elbrus", continent: "Europe", elevation: 5642, done: false },
  { peak: "Vinson", continent: "Antarctica", elevation: 4892, done: false },
  { peak: "Puncak Jaya (Carstensz)", continent: "Oceania", elevation: 4884, done: false },
];

function SummitTracker() {
  const climbed = SUMMITS.filter((s) => s.done).length;
  const [sponsorPeak, setSponsorPeak] = useState(null);
  return (
    <section data-testid="summit-tracker" className="bg-white border-y border-ptf-primary/10 py-16 md:py-24">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <Eyebrow>The Seven Summits</Eyebrow>
            <MaskText
              lines={["One climbed.", "Six waiting."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
          </div>
          <p data-testid="summit-progress" className="font-display uppercase text-2xl tracking-tight text-ptf-primary shrink-0">
            {climbed} <span className="text-ptf-text/40">of</span> 7
          </p>
        </div>
        <div className="border-t-2 border-ptf-primary/15">
          {SUMMITS.map((s, i) => (
            <Reveal key={s.peak} delay={i * 0.04}>
              <div
                data-testid={`summit-row-${s.peak.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                className={`grid grid-cols-12 items-center gap-3 md:gap-5 py-5 border-b border-ptf-primary/10 ${s.done ? "bg-ptf-accent/10 -mx-4 px-4" : ""}`}
              >
                <div className="col-span-2 sm:col-span-1">
                  {s.done ? (
                    <span className="grid place-items-center h-9 w-9 rounded-full bg-ptf-accent text-ptf-primary"><Check className="h-5 w-5" /></span>
                  ) : (
                    <span className="grid place-items-center h-9 w-9 rounded-full border-2 border-dashed border-ptf-primary/30" />
                  )}
                </div>
                <div className="col-span-10 sm:col-span-4">
                  <p className="font-display uppercase text-xl md:text-2xl tracking-tight text-ptf-primary leading-none">{s.peak}</p>
                  <p className="mt-1 text-[0.62rem] uppercase tracking-[0.18em] text-ptf-text/50">{s.continent} · {s.elevation.toLocaleString("en-IN")} m</p>
                </div>
                <div className="col-span-8 sm:col-span-4 col-start-3 sm:col-start-auto">
                  <div className="h-1.5 bg-ptf-primary/8 overflow-hidden">
                    <div className={`h-full ${s.done ? "bg-ptf-accent" : "bg-ptf-primary/25"}`} style={{ width: `${Math.round((s.elevation / 8849) * 100)}%` }} />
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-3 sm:text-right">
                  {s.done ? (
                    <span className="text-[0.62rem] uppercase tracking-[0.14em] text-ptf-primary font-semibold">{s.note}</span>
                  ) : (
                    <button
                      type="button"
                      data-testid={`sponsor-btn-${s.peak.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                      onClick={() => setSponsorPeak(s)}
                      className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.14em] font-semibold text-ptf-secondary hover:text-ptf-primary border border-ptf-secondary/30 hover:border-ptf-primary px-3 py-1.5 transition-colors"
                    >
                      Sponsor this climb <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-5 text-[0.65rem] text-ptf-text/50 leading-relaxed">
          Elevations are the accepted figures for each peak. Only Kilimanjaro (2023) is
          claimed as climbed; every future summit will be documented here when it happens.
        </p>
        <SponsorClimbDialog peak={sponsorPeak} onClose={() => setSponsorPeak(null)} />
      </div>
    </section>
  );
}

export default function HighAltitude() {
  return (
    <>
      <Seo title="Project Dawn: High Altitude" path="/dawn/high-altitude" description="A valley's flag on the world's roofs: Project Dawn's high-altitude mission, begun in 2019, summited Kilimanjaro in 2023. The remaining Seven Summits and 8000-metre peaks await partners and sponsors." />
      <PageHero
        eyebrow="Project Dawn · Ongoing mission"
        titleLines={["High", "altitude."]}
        intro="A valley's flag on the world's roofs. An ongoing mission that began with Project Dawn in 2019, connecting adventure, discipline and sustainable development."
        image="/dawn/kilimanjaro-summit.jpg"
      />

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>The mission</Eyebrow></Reveal>
            <MaskText
              lines={["A valley's flag on", "the world's roofs."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
            <Reveal delay={0.15}>
              <p className="mt-8 text-lg text-ptf-text/80 leading-relaxed">
                The mission began with Project Dawn in 2019: carry the story of rural
                Uttarakhand to the world's great mountains. In 2023 it reached its
                first of the Seven Summits: Uhuru Peak, Mount Kilimanjaro, 5,895
                metres, the highest point in Africa, with the Project Dawn flag held
                against the summit sign.
              </p>
              <p className="mt-6 text-ptf-text/70 leading-relaxed">
                The remaining Seven Summits are still ahead, along with the
                8000-metre giants. Reaching them needs collaboration, sponsorship and
                partners. The mission is open to all three.
              </p>
              <p className="mt-6 text-ptf-text/70 leading-relaxed inline-flex items-start gap-2.5">
                <Film className="h-5 w-5 mt-0.5 shrink-0 text-ptf-secondary" />
                The mission is being filmed as it happens: a documentary will be made
                from the climbs, alongside the Dawn film.
              </p>
              <div className="mt-8 flex flex-wrap gap-2" data-testid="ha-themes">
                {THEMES.map((t) => (
                  <span key={t} className="text-[0.7rem] uppercase tracking-[0.12em] border border-ptf-primary/25 text-ptf-primary px-3 py-1.5">{t}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <figure>
                <img src="/dawn/kilimanjaro-summit.jpg" alt="The Project Dawn flag at Uhuru Peak, Mount Kilimanjaro, 5895m, 2023" className="w-full object-cover" loading="lazy" />
                <figcaption className="mt-2 flex items-center justify-between gap-3 text-[0.65rem] uppercase tracking-[0.12em] text-ptf-text/55">
                  Uhuru Peak, Kilimanjaro · 2023 · Project Dawn flag
                  <EvidenceChip status="DOCUMENTED" />
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      <SummitTracker />

      <section className="bg-ptf-primary text-ptf-bg py-16 md:py-24 grain">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow light>The road ahead</Eyebrow></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              ["2019 · The start", "The high-altitude mission begins under Project Dawn, with mountains as the message-carrier for the valleys the project works in."],
              ["2023 · Kilimanjaro", "The first of the Seven Summits climbed and documented, flag on the summit sign at 5,895 metres."],
              ["Next · Six summits & the 8000ers", "The remaining Seven Summits and the 8000-metre peaks are pending. They need collaboration, sponsorship and partners, and every climb will be filmed."],
            ].map(([t, x], i) => (
              <Reveal key={t} delay={i * 0.07}>
                <div className="border-t border-ptf-bg/20 pt-5">
                  <h3 className="font-display uppercase text-2xl tracking-tight">{t}</h3>
                  <p className="mt-3 text-sm text-ptf-bg/70 leading-relaxed">{x}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ptf-secondary text-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">Six summits are waiting<br />for a partner.</h2>
            <p className="mt-4 text-ptf-bg/70 max-w-xl leading-relaxed text-sm">Sponsor a climb, join the collaboration, or back the documentary that will carry it to screens.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PtfButton to="/partnerships" variant="accent" data-testid="ha-partner-cta">Partner on the Mission</PtfButton>
            <PtfButton to="/dawn" variant="outlineLight">Back to Project Dawn</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
